'use client';

import { ReactNode, useEffect, useState } from 'react';

/**
 * Client provider that starts the MSW **browser worker** before rendering the app, so CSR Apollo
 * queries never race the interceptor. Integration test harness only.
 *
 * Prod safety: the gate combines `process.env.NODE_ENV` — which Next **always** inlines as the literal
 * `'production'` in production builds — with the public mocking flag. In a prod build the
 * `NODE_ENV !== 'production'` term folds to `false`, so `ENABLE` is a constant `false`, the `useEffect`
 * body is unreachable, and the dynamic `import('.../mocks/browser')` is dead-code-eliminated — MSW
 * (and its ~240 kB browser bundle) is never emitted into the production client bundle. When the flag is
 * off, `ready` starts `true`, so children render immediately (behaviour identical to today).
 *
 * When enabled (only under `next dev` with the flag set): `ready` starts `false` on both server and
 * client (no hydration mismatch — both render `null` initially); the worker starts on mount, then
 * children render. Server components still execute server-side (their RSC payload is carried in
 * `children`), so SSR data (mocked by MSW-node) is intact.
 *
 * Startup **fails closed**: if the worker cannot start, the app is not rendered at all. Rendering it
 * unmocked would let real cross-origin traffic escape — most damagingly the ConfigCat SDK, which the
 * integration harness configures with a non-hermetic key precisely so MSW can serve feature flags; an
 * unmocked run would silently reach the real CDN and resolve flags to values no spec asked for. A
 * visible fatal message fails specs fast and unambiguously instead.
 */
const ENABLE = process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_API_MOCKING === '1';

interface MSWProviderProps {
  readonly children: ReactNode;
}

/** Rendered instead of the app when the worker fails; asserted by the harness, greppable in CI logs. */
const MSW_STARTUP_FAILED_TEST_ID = 'msw-startup-failed';

export function MSWProvider({ children }: MSWProviderProps) {
  const [ready, setReady] = useState(!ENABLE);
  const [startupFailed, setStartupFailed] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_API_MOCKING !== '1') return undefined;

    let active = true;
    void (async () => {
      try {
        const [{ worker }, { onUnhandledRequest }] = await Promise.all([
          import('../../msw-browser-entry'),
          import('../../msw-unhandled-request-entry'),
        ]);
        // Selective strategy: bypass localhost/static internals, THROW on unmocked cross-origin backend
        // calls so the suite stays hermetic.
        await worker.start({ onUnhandledRequest, quiet: true });
      } catch (error) {
        // Fail closed. Rendering the app unmocked would break the suite's offline guarantee and let
        // ConfigCat resolve flags from the real CDN, so surface a fatal harness error instead.
        // eslint-disable-next-line no-console
        console.error('[MSW] browser worker failed to start; refusing to render the app unmocked', error);
        if (active) setStartupFailed(true);
      } finally {
        if (active) setReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  if (startupFailed) {
    return (
      <div
        role="alert"
        data-testid={MSW_STARTUP_FAILED_TEST_ID}
      >
        MSW browser worker failed to start — refusing to render the app unmocked. See the browser console.
      </div>
    );
  }

  if (!ready) return null;

  return <>{children}</>;
}
